// Documentos do edital na tela da licitação (13/10/2026): de onde veio cada
// um, em que ordem aparecem, o que dizer quando não há nenhum — e as datas do
// edital ditas do jeito certo. Lógica pura; a tela só chama.
//
// Lucas: "as oportunidades públicas estão vindo sem PDF e não sei como pedir na
// UI para buscá-los. Canela, por exemplo, além de não ter os PDFs está com a
// data desatualizada." A data "desatualizada" era o campo "Data abertura": no
// PNCP ele guarda o dia em que o órgão COMEÇOU a receber propostas (Canela:
// 01/10), não o prazo (19/10 09:00). A regra do servidor está em
// lambda/comercial-engine/handlers/licitacoesDocumentos.js.

export interface DocumentoEdital {
  id: number;
  nome: string;
  tipo: string;
  s3_key: string | null;
  download_url?: string | null;
  vigente?: boolean | null;
  metadados?: any;
  criado_em?: string;
}

export interface EstadoDocumentos {
  /** De onde dá para buscar: 'pncp', 'conlicitacao' ou null (cadastrada à mão, sem link). */
  fonte: 'pncp' | 'conlicitacao' | null;
  link_fonte: string | null;
  /** Licitação aberta no quadro: a rotina da manhã confere sozinha. */
  automatica: boolean;
  ultima_busca: { id: number | string; resultado: string; resumo: string; criado_em: string; criado_por: string | null } | null;
}

const NOME_DA_FONTE: Record<string, string> = { pncp: 'PNCP', conlicitacao: 'ConLicitação' };
export const nomeDaFonte = (fonte?: string | null): string => NOME_DA_FONTE[String(fonte || '')] || 'fonte';

/** A etiqueta de origem do documento: quem pôs ele ali. */
export function origemDoDocumento(a: DocumentoEdital): { rotulo: string; cor: string } {
  const m = a.metadados || {};
  if (m.upload_manual === true) return { rotulo: 'anexado à mão', cor: 'orange' };
  if (m.origem === 'pncp' || m.pncp) return { rotulo: 'PNCP', cor: 'blue' };
  if (m.origem === 'conlicitacao' || a.tipo === 'edital_conlicitacao') return { rotulo: 'ConLicitação', cor: 'geekblue' };
  return { rotulo: '', cor: 'default' };
}

/** "4,2 MB", "318 KB" — ou null quando o servidor não guardou o tamanho. */
export function tamanhoDoDocumento(a: DocumentoEdital): string | null {
  const m = a.metadados || {};
  const n = Number(m.tamanho_bytes ?? m.size_bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  const mb = n / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1).replace('.', ',') : Math.round(mb)} MB`;
}

/**
 * Endereço do arquivo no site do órgão/PNCP (para o que não foi guardado aqui).
 * `url_real` vence: há portal que publica no PNCP só um bilhete com o endereço
 * do arquivo no site dele — abrir o endereço do PNCP baixaria o bilhete.
 */
export function linkNaFonte(a: DocumentoEdital): string | null {
  const u = a.metadados?.url_real || a.metadados?.pncp?.url || a.metadados?.pncp?.uri || a.metadados?.url_original || null;
  return typeof u === 'string' && /^https:\/\//i.test(u) ? u : null;
}

const diaMes = (parede?: string | null): string => {
  const m = String(parede || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : '';
};

// Quando o órgão publicou este arquivo no PNCP. No PNCP o nome do arquivo é
// muitas vezes o TIPO ("Aviso de alteração do Edital/Errata") — no Plano Diretor
// de Canela eram três erratas e três avisos com o mesmo nome. A data é o que
// diz qual é qual, e qual é a última.
const dataDaPublicacao = (a: DocumentoEdital): string => {
  const d = String(a.metadados?.pncp?.dataPublicacaoPncp || '');
  return /^\d{4}-\d{2}-\d{2}/.test(d) ? d : '';
};

/** "15/09/2026" — o dia em que o órgão publicou o arquivo na fonte; null quando não se sabe. */
export function publicadoEm(a: DocumentoEdital): string | null {
  const m = dataDaPublicacao(a).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/** O que há de diferente neste documento — ou null quando é um documento comum. */
export function avisoDoDocumento(a: DocumentoEdital): { texto: string; cor: string; dica: string } | null {
  const m = a.metadados || {};
  if (m.so_link) {
    return { texto: 'só o link', cor: 'gold',
      dica: `Não foi guardado aqui (${m.motivo_sem_arquivo || 'arquivo grande demais'}). Abra direto na fonte.` };
  }
  if (a.vigente === false && m.retirado_da_fonte_em) {
    return { texto: `retirado pelo órgão${diaMes(m.retirado_da_fonte_em) ? ` em ${diaMes(m.retirado_da_fonte_em)}` : ''}`, cor: 'red',
      dica: 'O órgão tirou este documento da publicação. Ele fica guardado, mas não entra mais no checklist nem na análise.' };
  }
  if (a.vigente === false) {
    return { texto: 'versão antiga', cor: 'default', dica: 'Substituído por uma versão mais nova. Não entra no checklist nem na análise.' };
  }
  return null;
}

// A mesma ordem do servidor (ORDEM_DO_PAPEL): o edital primeiro, a planta por último.
const ORDEM = ['edital', 'errata', 'esclarecimento', 'termo_referencia', 'projeto_basico', 'aviso', 'minuta',
  'etp', 'planilha', 'cronograma', 'riscos', 'memorial', 'projeto', 'outro'];
const PAPEL_PELO_TIPO: Array<[RegExp, string]> = [
  [/^edital/i, 'edital'], [/errata|altera/i, 'errata'], [/esclarec|impugna/i, 'esclarecimento'],
  [/termo de refer/i, 'termo_referencia'], [/projeto b/i, 'projeto_basico'], [/aviso/i, 'aviso'], [/minuta/i, 'minuta'],
  [/estudo t/i, 'etp'], [/planilha|bdi/i, 'planilha'], [/cronograma/i, 'cronograma'], [/risco/i, 'riscos'],
  [/memorial/i, 'memorial'], [/projeto|planta/i, 'projeto'],
];
function posicao(a: DocumentoEdital): number {
  let papel = String(a.metadados?.papel || '');
  if (!papel) papel = (PAPEL_PELO_TIPO.find(([re]) => re.test(a.tipo || '')) || [null, 'outro'])[1] as string;
  const i = ORDEM.indexOf(papel);
  return i < 0 ? ORDEM.length : i;
}

/**
 * Vigentes antes dos retirados; dentro disso, edital → erratas → TR → … → plantas;
 * no mesmo papel, o publicado por último primeiro (a errata que vale é a mais nova);
 * sem data, pelo nome.
 */
export function ordenarDocumentos<T extends DocumentoEdital>(lista: T[]): T[] {
  return [...(lista || [])].sort((x, y) =>
    (Number(x.vigente === false) - Number(y.vigente === false))
    || (posicao(x) - posicao(y))
    || dataDaPublicacao(y).localeCompare(dataDaPublicacao(x))
    || String(x.nome || '').localeCompare(String(y.nome || ''), 'pt-BR', { numeric: true }));
}

/** O texto da lista vazia: diz ONDE clicar (antes mandava a um botão de outra tela). */
export function textoSemDocumentos(estado?: EstadoDocumentos | null): { titulo: string; ajuda: string } {
  if (!estado || !estado.fonte) {
    return { titulo: 'Nenhum documento anexado.',
      ajuda: 'Esta licitação não tem link do PNCP nem veio da ConLicitação: cole o link do PNCP na aba Atualizações, ou clique em "Anexar PDF".' };
  }
  const onde = estado.fonte === 'pncp' ? 'no PNCP' : 'na ConLicitação';
  return { titulo: 'Nenhum documento por aqui ainda.',
    ajuda: `Clique em "Buscar na fonte" para trazer o edital e os anexos publicados ${onde}.${estado.automatica ? ' As licitações do quadro são conferidas sozinhas toda manhã.' : ''}` };
}

/** Cor do recado da última busca (Alert do antd). */
/** Resultado do botão "Atualizar" (14/10/2026): a última ida aos portais. */
export interface UltimaAtualizacao {
  id: number | string;
  resultado: 'novo' | 'nada' | 'parcial' | string;
  resumo: string;
  criado_em: string;
  criado_por: string | null;
}

/** Cor do recado do "Atualizar": novidade em verde, nada novo neutro, incompleta em amarelo. */
export function tipoDaAtualizacao(resultado?: string | null): 'success' | 'info' | 'warning' {
  if (resultado === 'novo') return 'success';
  if (resultado === 'parcial') return 'warning';
  return 'info';
}

export function tipoDoRecado(resultado?: string | null): 'success' | 'info' | 'warning' | 'error' {
  if (resultado === 'falhou') return 'error';
  if (resultado === 'parcial' || resultado === 'sem_fonte') return 'warning';
  if (resultado === 'sem_documentos') return 'info';
  return 'success';
}

// ── As datas do edital ──────────────────────────────────────────────────────

/** 'AAAA-MM-DDTHH:MM[:SS]' (parede de Brasília, sem fuso) → 'dd/mm/aaaa hh:mm'. Lê o TEXTO: nada de new Date(). */
export function fmtParede(parede?: string | null): string {
  const m = String(parede || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return '';
  const hora = m[4] && !(m[4] === '00' && m[5] === '00') ? ` ${m[4]}:${m[5]}` : '';
  return `${m[3]}/${m[2]}/${m[1]}${hora}`;
}

const mesmoMinuto = (a?: string | null, b?: string | null) =>
  String(a || '').replace(' ', 'T').slice(0, 16) === String(b || '').replace(' ', 'T').slice(0, 16);

export interface LinhaDeData { rotulo: string; valor: string; dica?: string }

/**
 * As datas do card "Dados do edital". Primeiro o PRAZO DA PROPOSTA (a régua do
 * servidor, `prazo_efetivo`), dizendo de onde veio. A "abertura" só aparece
 * quando é OUTRA data — e, no PNCP, com o nome do que ela é: o dia em que o
 * órgão começou a receber propostas.
 */
export function datasDoEdital(o: {
  fonte?: string | null; prazo_efetivo?: string | null; origem_prazo?: string | null; data_abertura?: string | null;
} | null | undefined): LinhaDeData[] {
  if (!o) return [{ rotulo: 'Prazo da proposta', valor: '—' }];
  const origem = String(o.origem_prazo || '');
  const dica = origem.startsWith('unificada:') ? `O mais atualizado entre as fontes (${nomeDaFonte(origem.slice(10))}).`
    : origem.startsWith('pncp:') ? 'Encerramento do recebimento de propostas informado pelo PNCP.'
      : origem.startsWith('conlicitacao:') ? 'Prazo informado no boletim da ConLicitação.'
        : origem.startsWith('fallback:') ? 'A fonte não informou o prazo: esta é a data de abertura cadastrada.' : undefined;
  const linhas: LinhaDeData[] = [{ rotulo: 'Prazo da proposta', valor: fmtParede(o.prazo_efetivo) || '—', dica: o.prazo_efetivo ? dica : undefined }];
  if (o.data_abertura && !mesmoMinuto(o.data_abertura, o.prazo_efetivo)) {
    linhas.push(o.fonte === 'pncp'
      ? { rotulo: 'Recebe propostas desde', valor: fmtParede(o.data_abertura), dica: 'No PNCP esta é a data em que o órgão começou a receber propostas — não é o prazo.' }
      : { rotulo: 'Abertura informada', valor: fmtParede(o.data_abertura) });
  }
  return linhas;
}
